import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { XCLUSION_SELECTIONS } from '@src/types/Filters';
import { style } from 'typestyle';
import { CheckBoxCustom } from '@src/fragments/Filters/Search/Suggestions/InfluencersComponents/CheckBoxCustom';
import { CountryCheckbox } from '@src/fragments/Filters/Search/Suggestions/InfluencersComponents/CountryCheckbox';
import { useAtomValue } from 'jotai';
import { useQueryInfluencersCreators } from '@src/queries/influencers/useQueryCreators';
import { onFilterChange } from "@src/fragments/Filters/utils/FilterStateUtility";
import { creatorExcludeState } from "@src/pages/Discover/components/Selectors/Selectors";
import { XclusionUtility } from "@src/fragments/Filters/managers/XclusionUtility";
import { useFiltersParams } from '@src/hooks/useFiltersParams';

const GLOBAL_GROUP_KEY = 'GLOBAL';
const GLOBAL_GROUP_LABEL = 'Global';

export function InfluencerSuggestion() {
    const {
        data: ResInfluencers,
    } = useQueryInfluencersCreators();
    
    const { filters, setFilters } = useFiltersParams();
    const exclude = useAtomValue(creatorExcludeState);
    const xclusion = exclude ? XCLUSION_SELECTIONS[1] : XCLUSION_SELECTIONS[0];
    const [countrySelections, setCountrySelections] = useState<Record<string, boolean>>({});
    const [hasManualCountrySelection, setHasManualCountrySelection] = useState(false);

    // Build map of influencers by country
    const influencersByCountry = useMemo(() => {
        const map = new Map<string, string[]>();
        const allInfluencers = new Set<string>();

        ResInfluencers?.forEach((inf) => {
            allInfluencers.add(inf.username);
            const hasCountries = inf.countries && inf.countries.length > 0;
            if (hasCountries) {
                inf.countries!.forEach((country) => {
                    const countryInfluencers = map.get(country) || [];
                    countryInfluencers.push(inf.username);
                    map.set(country, countryInfluencers);
                });
            } else {
                const globalInfluencers = map.get(GLOBAL_GROUP_KEY) || [];
                globalInfluencers.push(inf.username);
                map.set(GLOBAL_GROUP_KEY, globalInfluencers);
            }
        });

        // Add ALL group
        map.set('ALL', Array.from(allInfluencers));

        return map;
    }, [ResInfluencers]);

    // Get sorted country list (excluding ALL for now)
    const countries = useMemo(() => {
        const keys = Array.from(influencersByCountry.keys()).filter((key) => key !== 'ALL');
        const hasGlobal = keys.includes(GLOBAL_GROUP_KEY);
        const sortedKeys = keys.filter((key) => key !== GLOBAL_GROUP_KEY).sort();
        if (hasGlobal) {
            return [GLOBAL_GROUP_KEY, ...sortedKeys];
        }
        return sortedKeys;
    }, [influencersByCountry]);

    // Get all influencers
    const allInfluencers = influencersByCountry.get('ALL') || [];

    // Check if all influencers are selected
    const areAllSelected = useMemo(() => {
        if (allInfluencers.length === 0) return false;
        return allInfluencers.every(username => 
            filters.creator.some(c => XclusionUtility.getRawKey(c) === username)
        );
    }, [allInfluencers, filters.creator]);

    // Handle "All" checkbox toggle
    const handleAllToggle = (checked: boolean) => {
        onFilterChange(setFilters, 'creator')(allInfluencers, checked, xclusion);
        setHasManualCountrySelection(true);
        setCountrySelections(countries.reduce<Record<string, boolean>>((acc, country) => {
            acc[country] = checked;
            return acc;
        }, {}));
    };

    // Handle individual country toggle
    const handleCountryToggle = (country: string, checked: boolean) => {
        const countryInfluencers = influencersByCountry.get(country) || [];
        if (checked) {
            onFilterChange(setFilters, 'creator')(countryInfluencers, true, xclusion);
            setHasManualCountrySelection(true);
            setCountrySelections((prev) => ({ ...prev, [country]: true }));
            return;
        }

        // When unchecking a country, keep influencers that still belong to another selected country
        const influencersToKeep = new Set<string>();
        countries.forEach((otherCountry) => {
            if (otherCountry === country) {
                return;
            }
            const otherCountryInfluencers = influencersByCountry.get(otherCountry) || [];
            const otherSelected = otherCountryInfluencers.length > 0 && otherCountryInfluencers.every((username) =>
                filters.creator.some((c) => XclusionUtility.getRawKey(c) === username)
            );
            if (otherSelected) {
                otherCountryInfluencers.forEach((username) => influencersToKeep.add(username));
            }
        });

        const influencersToRemove = countryInfluencers.filter((username) => !influencersToKeep.has(username));
        if (influencersToRemove.length) {
            onFilterChange(setFilters, 'creator')(influencersToRemove, false, xclusion);
        }
        setHasManualCountrySelection(true);
        setCountrySelections((prev) => ({ ...prev, [country]: false }));
    };

    // Handle individual influencer toggle  
    const handleInfluencerToggle = (username: string) => (checked: boolean) => {
        onFilterChange(setFilters, 'creator')([username], checked, xclusion);
    };

    // Check if a specific influencer is selected
    const isInfluencerSelected = (username: string) => {
        return filters.creator.some(c => XclusionUtility.getRawKey(c) === username);
    };

    // Check if all influencers in a country are selected
    const areAllInCountrySelected = useCallback((country: string) => {
        const countryInfluencers = influencersByCountry.get(country) || [];
        if (countryInfluencers.length === 0) return false;
        return countryInfluencers.every((username) =>
            filters.creator.some((c) => XclusionUtility.getRawKey(c) === username)
        );
    }, [filters.creator, influencersByCountry]);

    useEffect(() => {
        if (!countries.length) {
            setCountrySelections({});
            return;
        }

        if (!hasManualCountrySelection) {
            setCountrySelections((prev) => {
                const next = countries.reduce<Record<string, boolean>>((acc, country) => {
                    acc[country] = areAllInCountrySelected(country);
                    return acc;
                }, {});

                const prevKeys = Object.keys(prev);
                if (prevKeys.length === countries.length) {
                    const identical = countries.every((country) => prev[country] === next[country]);
                    if (identical) {
                        return prev;
                    }
                }

                return next;
            });
            return;
        }

        const countrySet = new Set(countries);
        setCountrySelections((prev) => {
            let next = prev;
            let changed = false;

            Object.keys(prev).forEach((country) => {
                if (!countrySet.has(country)) {
                    if (!changed) {
                        next = { ...next };
                    }
                    delete next[country];
                    changed = true;
                }
            });

            countries.forEach((country) => {
                if (!(country in next)) {
                    if (!changed) {
                        next = { ...next };
                        changed = true;
                    }
                    next[country] = false;
                }
            });

            countries.forEach((country) => {
                if (next[country] && !areAllInCountrySelected(country)) {
                    if (!changed) {
                        next = { ...next };
                        changed = true;
                    }
                    next[country] = false;
                }
            });

            return changed ? next : prev;
        });
    }, [areAllInCountrySelected, countries, hasManualCountrySelection]);

    useEffect(() => {
        if (filters.creator.length === 0) {
            setHasManualCountrySelection(false);
            setCountrySelections({});
        }
    }, [filters.creator.length]);

    return (
        <div className={containerClass}>
            {/* "All" checkbox */}
            <CheckBoxCustom
                checked={areAllSelected}
                onChange={handleAllToggle}
                negative={areAllSelected && exclude}
            >
                <span>All ({allInfluencers.length})</span>
            </CheckBoxCustom>

            {/* Country dropdowns */}
            {countries.map((country) => {
                const countryInfluencers = influencersByCountry.get(country) || [];
                const storedSelection = countrySelections[country];
                const isCountrySelected = storedSelection !== undefined ? storedSelection : areAllInCountrySelected(country);
                const displayName = country === GLOBAL_GROUP_KEY ? GLOBAL_GROUP_LABEL : country;
                
                return (
                    <CountryCheckbox
                        key={country}
                        country={displayName}
                        count={countryInfluencers.length}
                        checked={isCountrySelected}
                        onChange={(checked) => handleCountryToggle(country, checked)}
                        negative={isCountrySelected && exclude}
                        isOpen={false}
                    >
                        {countryInfluencers.map((username) => (
                            <CheckBoxCustom
                                key={username}
                                checked={isInfluencerSelected(username)}
                                onChange={handleInfluencerToggle(username)}
                                negative={isInfluencerSelected(username) && exclude}
                            >
                                <span>{username}</span>
                            </CheckBoxCustom>
                        ))}
                    </CountryCheckbox>
                );
            })}
        </div>
    );
}

const containerClass = style({
    marginTop: 10,
    fontWeight: 'inherit',
});

